/**
 * GENERATED by /docs-writer from docs/transforge-clients.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/transforge-clients.md`.
 *
 * source-sha: 306e41262f2d6b729ed9688648529ca4d0037dd8
 * sources (from the markdown's "Sources" section):
 *   - Live production scan, 2026-10-12: Deployment env and ConfigMaps of every client workload, read with kubectl
 *     (read-only)
 *   - TransForge hourly stats rollup (Postgres database transforge, table assembly_stats), 2026-10-05 to 2026-10-11
 *   - os-transforge docs/clients/README.md and the per-client pages (verified 2026-10-01 against the running pods),
 *     and docs/clients/canvas-animation-worker.md (merge request 212, read from the running pods on 2026-10-12)
 *   - os-transforge packages/transforge-sdk/package.json, packages/uppy-transforge/package.json, and the project's
 *     empty GitLab package registry (read 2026-10-12)
 *   - conversation, 2026-10-12
 */
import { Link } from 'react-router';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'who-calls-transforge', label: 'Who calls TransForge' },
  { id: 'which-clients-are-safe-behind-the-global-name', label: 'Which clients are safe behind the global name' },
  { id: 'what-must-change-before-a-client-moves', label: 'What must change before a client moves' },
  { id: 'how-clients-talk-to-transforge', label: 'How clients talk to TransForge' },
];

// Matrix cells: the mark is drawn in CSS, the word is for screen readers.
const YES = (
  <span className="doc-yes">
    <span className="doc-vh">allowed</span>
  </span>
);
const NO = (
  <span className="doc-no">
    <span className="doc-vh">blocked</span>
  </span>
);

export function TransforgeClientsDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Media · Reference</p>
        <h1 className="doc-title">TransForge clients</h1>
        <p className="doc-lede">
          Eleven production clients create assemblies on <Link to="/docs/transforge">TransForge</Link>, and one
          service receives its results. Ten of the eleven call the DigitalOcean host directly.{' '}
          <strong>Giphy</strong> is the only client on the global name that can answer from either cloud. The{' '}
          <strong>reporting engine</strong> must change before it can move, because it builds its own status URL. The
          figures on this page were read from production on 12 October 2026.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-14">14 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="who-calls-transforge">Who calls TransForge</h2>
        <p className="doc-p">
          A client either creates assemblies or receives the result that TransForge posts back. The share is each
          client&rsquo;s part of all assemblies created in the seven days from 5 to 11 October 2026.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>How the file reaches TransForge</th>
                <th>Host it calls</th>
                <th className="doc-num">Share</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Google Drive job</strong> (Sheets, Docs)</td>
                <td>TransForge fetches it from Google</td>
                <td>DigitalOcean</td>
                <td className="doc-num">47%</td>
              </tr>
              <tr>
                <td><strong>Google Slides job</strong></td>
                <td>TransForge fetches it from Google</td>
                <td>DigitalOcean</td>
                <td className="doc-num">23%</td>
              </tr>
              <tr>
                <td><strong>Portal web upload</strong></td>
                <td>The browser uploads it in resumable chunks</td>
                <td>DigitalOcean</td>
                <td className="doc-num">13%</td>
              </tr>
              <tr>
                <td><strong>Screenshot-website job</strong></td>
                <td>TransForge renders the page itself</td>
                <td>DigitalOcean</td>
                <td className="doc-num">7%</td>
              </tr>
              <tr>
                <td><strong>Canvas</strong> (copy files)</td>
                <td>TransForge fetches it from a URL</td>
                <td>DigitalOcean</td>
                <td className="doc-num">6%</td>
              </tr>
              <tr>
                <td><strong>OneDrive job</strong> (Excel, PowerPoint)</td>
                <td>TransForge fetches it from Microsoft</td>
                <td>DigitalOcean</td>
                <td className="doc-num">2%</td>
              </tr>
              <tr>
                <td><strong>Canvas animation</strong></td>
                <td>A renderer uploads the video in resumable chunks</td>
                <td>DigitalOcean</td>
                <td className="doc-num">1%</td>
              </tr>
              <tr>
                <td><strong>Reporting engine</strong></td>
                <td>TransForge renders the page, or the engine sends a PDF</td>
                <td>DigitalOcean</td>
                <td className="doc-num">&lt;1%</td>
              </tr>
              <tr>
                <td><strong>Mobile app</strong></td>
                <td>The app sends it in one request</td>
                <td>DigitalOcean</td>
                <td className="doc-num">&lt;1%</td>
              </tr>
              <tr>
                <td><strong>Device screenshots</strong> (MDM)</td>
                <td>The device sends it in one request</td>
                <td>DigitalOcean</td>
                <td className="doc-num">&lt;1%</td>
              </tr>
              <tr>
                <td><strong>Giphy</strong></td>
                <td>TransForge fetches it from a URL</td>
                <td>Global name</td>
                <td className="doc-num">&lt;1%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The three Google and Microsoft jobs create 72% of all assemblies. The portal upload is 13% of them, and it
          processes more bytes than any other client: 2.9 TB in those seven days.
        </p>
        <p className="doc-p">
          <strong>upload-processor</strong> is the one service that only receives. Every client except the reporting
          engine asks TransForge to post the result to it when an assembly ends, and it then updates the asset in the
          portal. The reporting engine sets no callback and polls for the result.
        </p>

        <h2 className="doc-h2" id="which-clients-are-safe-behind-the-global-name">
          Which clients are safe behind the global name
        </h2>
        <p className="doc-p">
          A client is safe when every request after the create goes to a URL that TransForge returned.{' '}
          <Link to="/docs/transforge-multi-cloud">Multi-cloud</Link> explains why. A client that sends one request for
          each assembly is safe by construction. A client that builds a URL from its configured host is unsafe,
          because that request can reach the cloud that does not hold the assembly.
        </p>
        <p className="doc-p">In the table, ✓ means yes and – means no.</p>
        <div className="doc-table doc-matrix">
          <table>
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">One request per assembly</th>
                <th scope="col">Builds a URL itself</th>
                <th scope="col">Safe behind the global name</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Google Drive job</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Google Slides job</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">OneDrive job</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Screenshot-website job</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Giphy</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Canvas (copy files)</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Mobile app</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Device screenshots (MDM)</th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Portal web upload</th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Canvas animation</th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Reporting engine</th>
                <td>{NO}</td>
                <td>{YES}</td>
                <td>{NO}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The portal upload and the Canvas animation renderer send more than one request. Both upload to the address in
          the create response, so they stay on the cloud that created the assembly.
        </p>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            Do not point the reporting engine at the global name. It polls{' '}
            <code>&lt;its configured host&gt;/assemblies/&lt;id&gt;</code> every 1.5 seconds. When the create lands on
            one cloud and a poll lands on the other, the engine reads an assembly that the second cloud does not hold.
          </p>
        </aside>

        <h2 className="doc-h2" id="what-must-change-before-a-client-moves">What must change before a client moves</h2>
        <p className="doc-p">
          Moving a safe client is one setting: the TransForge host in that client&rsquo;s configuration. These points
          decide the order.
        </p>
        <ul className="doc-list">
          <li>
            <strong>The reporting engine needs a code change first.</strong> It must poll the status URL from the create
            response in both places where it polls today. It has no fallback, so a TransForge outage is a reporting
            outage.
          </li>
          <li>
            <strong>Canvas animation takes its host from a queue message.</strong> A scheduler and Canvas write the host
            into each render job, and the renderer calls whatever host the job names. Change the host in those two
            services. The renderer has no TransForge setting of its own.
          </li>
          <li>
            <strong>The portal&rsquo;s Companion address stays on DigitalOcean.</strong> Companion fetches files from a
            customer&rsquo;s cloud drive for the portal upload, and it runs on DigitalOcean only.
          </li>
          <li>
            <strong>Five clients hide TransForge errors.</strong> The Drive, Slides, OneDrive, Screenshot and Giphy
            clients send a failed create to Transloadit and raise no error. A broken cloud shows up as Transloadit
            spend. Watch that spend while you raise the OCI share.
          </li>
          <li>
            <strong>Every cloud needs the same reach.</strong> A cloud that takes a client must reach Google, Microsoft
            Graph and customer URLs, and it must reach upload-processor to post results.
          </li>
        </ul>

        <h2 className="doc-h2" id="how-clients-talk-to-transforge">How clients talk to TransForge</h2>
        <p className="doc-p">No production client uses a TransForge SDK. Each one speaks HTTP in its own way.</p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client group</th>
                <th>Library</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Server jobs and REST services</td>
                <td>Plain HTTP requests with a signed body</td>
              </tr>
              <tr>
                <td>Canvas animation renderer</td>
                <td>The Transloadit SDK for Node, version 3.0.2, pointed at TransForge</td>
              </tr>
              <tr>
                <td>Portal web upload</td>
                <td>The Uppy Transloadit plugin, version 3.1.2</td>
              </tr>
              <tr>
                <td>Mobile app</td>
                <td>The Dio HTTP client</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The TransForge repository holds two client packages: an SDK for Node at version 1.0.0 and an Uppy plugin at
          version 0.1.0. Neither is published to a package registry, so no other repository can install them. The SDK
          uploads to the address that TransForge returns. It has no helper yet that reads the owning cloud from an
          assembly ID.
        </p>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Planning to move a client?</h2>
            <p>Read how the global name picks a cloud and what a read on the other cloud returns.</p>
          </div>
          <Link className="btn" to="/docs/transforge-multi-cloud">
            Read Multi-cloud
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
